export enum ProgrammingTrack {
  AtHomeTrack = 'At Home',
  AffiliateTrack = 'Affiliate',
  CompeteTrack = 'Compete',
  LiftingTrack = 'Lifting',
}

export enum ProgrammingClassType {
  RestDay = 'Rest Day',
  Benchmark = 'Benchmark',
  HeavyDay = 'Heavy Day',
  Partner = 'Partner',
}

export enum Score {
  Time = 'time',
  Load = 'load',
  RepsRounds = 'reps_rounds',
  Completion = 'completion',
  Calories = 'calories',
  Distance = 'distance',
}

export type Link = {
  text: string
  url: string
}

type HTMLTextComponent = {
  acfFcLayout: 'wysiwyg'
  body: string
  title?: string
}

type AtAGlanceComponent = {
  acfFcLayout: 'at_a_glance'
  items: { item: string }[]
  title: string
}

type TableComponent = {
  acfFcLayout: 'table'
  table: {
    body: { c: string }[][]
    header: { c: string }[]
  }
}

export type ClassPlanComponents =
  | HTMLTextComponent
  | AtAGlanceComponent
  | TableComponent

export type ClassPlan = {
  components: ClassPlanComponents[]
  duration: number
  hourTime: number
  title: string
}

export type DailyClassPlanContent = {
  classPlan: {
    components: ClassPlanComponents[]
    duration: string
    title: string
  }[]
  programmingTrack: ProgrammingTrack
  programmingClassType: ProgrammingClassType
  date: string
  fullPath: string
  name: string
}

type BaseDailyClassPlanContent = {
  name: string
  type: ProgrammingClassType
  date: Date
  slug: string
  slugs: {
    weeklyOverview: string
    nextDay?: string
    previousDay?: string
    affiliateTrack: string
    atHomeTrack: string
    competeTrack: string
    liftingTrack: string
  }
}

export type AtHomeWorkout = {
  description: string
  level: string
  score?: Score
}

export type AtHomeDailyClassPlanContent = BaseDailyClassPlanContent & {
  track: ProgrammingTrack.AtHomeTrack
  workouts?: AtHomeWorkout[]
  accessoryWork?: {
    description: string
    title: string
  }[]
  videoOverview?: {
    youtubeId: string
  }
}

export type CompeteWorkout = {
  description: string
  level: string
  notes?: {
    content?: string
    modifications?: {
      movement: string
      substitution: string
    }[]
    stimulusAndGoals?: string
    strategy?: string
  }
  title: string
}

export type CompeteDailyClassPlanContent = BaseDailyClassPlanContent & {
  track: ProgrammingTrack.CompeteTrack
  dailyFocus?: string
  dailyTip?: string
  stretching?: {
    description: string
  }
  warmUp?: {
    description: string
    notes: string
  }
  workoutOrder?: {
    element: string
    timeEstimate: string
  }[]
  workouts?: CompeteWorkout[]
}

export type LiftingWorkout = {
  title: string
  description: string
  notes: string
  score?: Score
}

export type LiftingDailyClassPlanContent = BaseDailyClassPlanContent & {
  track: ProgrammingTrack.LiftingTrack
  accessoryWork: { title: string; description: string }[]
  workouts: LiftingWorkout[]
}

export type AffiliateWorkout = {
  description: string
  level: string
  score?: Score
}

export type AffiliateDailyClassPlanContent = BaseDailyClassPlanContent & {
  track: ProgrammingTrack.AffiliateTrack
  aboutTheWorkout?: {
    coachingGoals: { goal: string }[]
    intendedStimulus: { stimulus: string }[]
    videoOverview: {
      youtubeId: string
      linkText: string
    }
  }
  logistics?: {
    limitations: string
    otherConsiderations: string
  }
  scaling?: {
    movementScalingOptions?: {
      movement: string
      substitutions: string
    }[]
    overview: string
  }
  videoResources?: {
    title: string
    youtubeId: string
  }[]
  workouts?: AffiliateWorkout[]
  accessoryWork?: {
    description: string
    title: string
  }[]
  classPlan?: ClassPlan[]
}

export type DailyClassPlan =
  | AtHomeDailyClassPlanContent
  | AffiliateDailyClassPlanContent
  | LiftingDailyClassPlanContent
  | CompeteDailyClassPlanContent

export enum MembershipStatus {
  formerPayingCustomer,
  formerBetaCustomer,
  newCustomer,
  currentBetaCustomer,
  currentPayingCustomer,
  starterKitCustomer,
}
